'use client'

import useI18n from 'Hooks/useI18n'
import { CodeBlock, ScrollableBlock } from './GuideShared'
import GuideModalShell from './GuideModalShell'

type OutputGuideModalProps = {
  open: boolean
  onClose: () => void
}

function Page1() {
  const { t } = useI18n()
  return (
    <div>
      <h2 className="text-xl font-semibold text-white">
        {t('What is Output Setting?')}
      </h2>
      <div className="mt-4 space-y-3 text-sm leading-6 text-white/70">
        <p>
          {t(
            'Output Setting tells the AI what content and format each reply should contain.'
          )}
        </p>
        <p>
          {t(
            'If you do not set it, the AI will reply using the format from the Greeting by default.'
          )}
        </p>
        <p>
          {t(
            'Setting the output format ensures every reply maintains a consistent structure, for example:'
          )}
        </p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li>{t('Always include narration + dialogue')}</li>
          <li>{t('Always include a status panel')}</li>
          <li>{t('Control the proportion of inner monologue')}</li>
        </ul>
      </div>
    </div>
  )
}

function Page2() {
  const { t } = useI18n()

  // i18n-ignore: HTML demo template showing example output format; content mirrors the Leonhardt example character
  const htmlCode = `<html-box>
<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Leonhardt Strategy Status Card</title>
<style>
.nc{max-width:320px;height:550px;margin:0 auto;background:#0d1117;border-radius:20px;border:1px solid #6366f1;box-shadow:0 0 20px rgba(99,102,241,0.2),inset 0 0 30px rgba(99,102,241,0.05);overflow-y:auto;padding:20px 16px;font-family:"Courier New",monospace;color:#e0e7ff;position:relative}
.nc::-webkit-scrollbar{width:5px}.nc::-webkit-scrollbar-track{background:#161b22}.nc::-webkit-scrollbar-thumb{background:#6366f1;border-radius:3px}
.nc::before{content:"\\2756";display:block;text-align:center;font-size:24px;margin-bottom:4px;text-shadow:0 0 12px rgba(99,102,241,0.6);color:#a78bfa}
.nt{text-align:center;font-size:18px;font-weight:700;color:#a78bfa;text-transform:uppercase;letter-spacing:3px;margin-bottom:16px;padding-bottom:10px;border-bottom:1px solid rgba(99,102,241,0.3);text-shadow:0 0 10px rgba(167,139,250,0.5)}
.ns{margin-bottom:14px;padding:12px;background:rgba(99,102,241,0.08);border-radius:12px;border:1px solid rgba(99,102,241,0.2)}
.nst{font-size:13px;color:#a78bfa;font-weight:700;margin-bottom:8px;text-transform:uppercase;letter-spacing:1px}
.nq{font-size:12px;color:#c4b5fd;line-height:1.6;font-style:italic;margin:5px 0;padding-left:10px;border-left:2px solid #6366f1}
.nsi{display:flex;align-items:center;margin:8px 0;font-size:13px}
.nsl{color:#a78bfa;font-weight:600;width:65px;font-size:12px}
.nb{flex:1;height:10px;background:#161b22;border-radius:5px;overflow:hidden;margin:0 8px;border:1px solid rgba(99,102,241,0.2)}
.nbf{height:100%;border-radius:4px;background:linear-gradient(90deg,#6366f1,#a78bfa);box-shadow:0 0 8px rgba(99,102,241,0.5)}
.nhf{height:100%;border-radius:4px;background:linear-gradient(90deg,#ef4444,#f87171);box-shadow:0 0 8px rgba(239,68,68,0.5)}
.nsv{color:#6366f1;font-weight:700;min-width:30px;text-align:right}
.ni{font-size:12px;color:#c4b5fd;line-height:1.6;padding-left:18px;position:relative;margin:4px 0}
.ni::before{content:"\\25B8";color:#6366f1;position:absolute;left:2px}
.ncc{font-size:12px;color:#c4b5fd;line-height:1.7}
</style>
</head>
<body>
<div class="nc">
  <div class="nt">Leonhardt's Strategy File</div>

  <div class="ns">
    <div class="nst">Classic Lines</div>
    <div class="nq">Duty is my armor, but only you can touch the warmth beneath it.</div>
    <div class="nq">The royal engagement can bind my body, but not my gaze that lingers on you.</div>
    <div class="nq">Do not fear. As long as I stand, no one shall harm you an inch.</div>
  </div>

  <div class="ns">
    <div class="nst">⚡ Core Stats</div>
    <div class="nsi">
      <span class="nsl">Progress</span>
      <div class="nb"><div class="nbf" style="width:35%"></div></div>
      <span class="nsv">35%</span>
    </div>
    <div class="nsi">
      <span class="nsl">HP</span>
      <div class="nb"><div class="nhf" style="width:90%"></div></div>
      <span class="nsv">90%</span>
    </div>
  </div>

  <div class="ns">
    <div class="nst">Inventory</div>
    <div class="ni">Silver pocket watch with family crest (his personal item, gifted to you)</div>
    <div class="ni">Handwritten letter from border barracks (unsent, addressed to you)</div>
    <div class="ni">Royal engagement invitation (he secretly folded a corner)</div>
    <div class="ni">Military ointment for wounds (prepared specially for you)</div>
    <div class="ni">Handmade scarf from you (hidden inside his military uniform)</div>
  </div>

  <div class="ns">
    <div class="nst">Royal News</div>
    <div class="ncc">Royal Announcement: The engagement ceremony of General Leonhardt and Princess Elena will be held next month. This is an important political marriage to consolidate the western border forces, attracting national attention.</div>
  </div>

  <div class="ns">
    <div class="nst">Engagement Related</div>
    <div class="ncc">1. Leonhardt holds a negative attitude towards the engagement and has delayed the preparation many times on the pretext of border military affairs.<br>2. Princess Elena fell in love with Leonhardt at first sight and proposed the engagement to the king on her own initiative.<br>3. The engagement cannot be terminated unilaterally and requires the king's handwritten approval or major changes.</div>
  </div>

  <div class="ns">
    <div class="nst">Opponents</div>
    <div class="ncc">1. Princess Elena: Royal blood, beautiful and arrogant, regards you as a rival and has sent people to investigate your identity many times.<br>2. Minister Kurt: The core supporter of the engagement, regards Leonhardt as a political chess piece and secretly suppresses you.<br>3. Border Rebel Leader: Tries to trigger royal civil strife by using the engagement and has targeted you who are related to Leonhardt.</div>
  </div>
</div>
</body></html>
</html-box>`

  return (
    <div>
      <h2 className="text-xl font-semibold text-white">
        {t('Manual Replacement Tutorial (After Loading Template)')}
      </h2>
      <div className="mt-4 space-y-3 text-sm leading-6 text-white/70">
        <p>
          {t(
            'After clicking "Load Official Template", the template contains a complete set of output format instructions and dynamic HTML template (default is vintage brown military style). You can quickly switch to a completely different visual style by replacing the content between annotation markers.'
          )}
        </p>
        <p className="font-semibold text-white">
          {t(
            'Replace: Switch status card from vintage brown to dark neon style'
          )}
        </p>
        <p>
          {t(
            'Find <!-- === Status Card Style Replace Start === --> and <!-- === Status Card Style Replace End === --> in the code, replace the entire HTML template between them with the code below:'
          )}
        </p>
      </div>
      <div className="mt-3">
        <CodeBlock code={htmlCode} />
      </div>
      <div className="mt-3 text-sm text-white/50">
        {t(
          'Effect: The status card attached at the end of each AI reply will change from vintage brown parchment style to dark neon tech style — same dynamic information, completely different visual feel. You can test the effect directly in the conversation after replacement.'
        )}
      </div>
    </div>
  )
}

function Page3() {
  const { t } = useI18n()

  // i18n-ignore: AI prompt kept in English for optimal LLM performance; users copy-paste this to AI
  const prompt = `Here is my character's status card HTML code:
（paste your status card HTML here）

Please convert this static HTML status card into a dynamic Prompt-format template:
- Replace all specific values (numbers, text content, dialogue lines) with descriptive {{PLACEHOLDER}} tags
- Keep the HTML structure and CSS styles unchanged
- Add instructions at the top telling the AI to:
  1. Write scene descriptions in <narration></narration> tags
  2. Write spoken dialogue in <message></message> tags (only what the character says, no speech descriptions)
  3. Include the complete HTML template in <html-box></html-box> tags with all {{PLACEHOLDER}} replaced by context-appropriate content
- Make sure the {{PLACEHOLDER}} names are clear enough for AI to understand what to fill in (e.g. {{AFFECTION_PERCENT}}, {{CURRENT_LOCATION}}, {{INVENTORY_ITEM_1}})

The output should be a complete prompt that I can paste into the "Output Settings" field, so the AI will format every reply consistently.`

  return (
    <div>
      <h2 className="text-xl font-semibold text-white">
        {t('AI Writes Output Setting for You (Official Prompt)')}
      </h2>
      <div className="mt-4 text-sm leading-6 text-white/70">
        <p>
          {t(
            'If you already have a status card HTML (e.g. the status card made in Greeting), you can use the Prompt below to have AI convert it into a dynamic template:'
          )}
        </p>
      </div>
      <div className="mt-3">
        <ScrollableBlock text={prompt} />
      </div>
      <div className="mt-3 text-sm text-white/50">
        {t(
          'Tip: First create a static status card in Greeting, then use this Prompt to convert it into a dynamic version.'
        )}
      </div>
    </div>
  )
}

const PAGES = [Page1, Page2, Page3]

export default function OutputGuideModal({
  open,
  onClose,
}: OutputGuideModalProps) {
  return <GuideModalShell open={open} onClose={onClose} pages={PAGES} />
}
